// Bounded-concurrency worker pool, shared by the bulk operations on both sides
// of the app: the server's bulk invoice retry and the Bills list's bulk refresh.
//
// Lives under features/ rather than server/ because the frontend needs it too,
// and the server already imports from here (shadowCalculationService). One pool,
// one set of semantics — two copies of "run N at a time" is how one of them ends
// up quietly abandoning the queue on the first throw.

/**
 * Run `task` over `items` with at most `concurrency` in flight.
 *
 * A fixed pool of workers pulling from a shared queue, rather than chunking into
 * fixed batches: a batch of three only moves on when its slowest member
 * finishes, so one 4-minute invoice idles two workers behind it. Invoice parse
 * times vary by an order of magnitude, so that matters.
 *
 * Never rejects. A task that throws is reported through `onError` and the pool
 * keeps going — one unparseable invoice must not abandon the other 107.
 */
export async function runPool<T>(
  items: T[],
  concurrency: number,
  task: (item: T) => Promise<void>,
  onError?: (item: T, err: unknown) => void,
): Promise<void> {
  const queue = [...items];
  const worker = async (): Promise<void> => {
    for (;;) {
      const item = queue.shift();
      if (item === undefined) return;
      try {
        await task(item);
      } catch (err) {
        onError?.(item, err);
      }
    }
  };
  const width = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: width }, () => worker()));
}
